/**
 * FRONTIER F9-4 — Adaptive copilot-scope publisher.
 *
 * The copilot rail is a lazily-loaded module (see `CopilotPanel` in AppShell); it
 * cannot import shell internals directly. So the shell PUBLISHES the current
 * entitlement-bounded copilot-scope config to a well-known, read-only global and
 * a subscribe channel. The copilot rail reads it to EMPHASIZE suggestions for
 * the persona's most-used, permitted modules — it does not change what the
 * copilot may access (the platform's own entitlement checks remain authoritative
 * on every copilot call); it only reorders emphasis.
 *
 * Publishing is a no-op-safe side channel: if nothing reads it, nothing breaks.
 */

import type { CopilotScopeConfig } from './adaptiveNav';

const GLOBAL_KEY = '__TYNDO_COPILOT_SCOPE__';
const EVENT_NAME = 'tyndo:copilot-scope';

interface ScopeGlobal {
  [GLOBAL_KEY]?: CopilotScopeConfig;
}

/**
 * Publish the current copilot-scope config so the copilot rail (and any other
 * consumer) can read the persona's emphasized, permitted module ordering. Fires
 * a DOM CustomEvent so a mounted rail can react without polling.
 */
export function publishCopilotScope(config: CopilotScopeConfig): void {
  if (typeof window === 'undefined') return;
  try {
    (window as unknown as ScopeGlobal)[GLOBAL_KEY] = config;
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: config }));
  } catch {
    /* best-effort side channel — never throw into the shell */
  }
}

/** Read the currently-published copilot scope, if any. */
export function readCopilotScope(): CopilotScopeConfig | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as ScopeGlobal)[GLOBAL_KEY];
}

export const COPILOT_SCOPE_EVENT = EVENT_NAME;
